import { deleteRequestWithBody, getRequest, postRequest, putRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import type Room from '../models/room/room';
import showStatusCodeError from './logging.service';

export const getAllRoomsByOfficeId = async (id: string, keycloak: KeycloakContext): Promise<Room[] | undefined> => {
  const apiResponse = await getRequest<Room[]>(`room/all-by-office-id?officeId=${id}`, keycloak);
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  return apiResponse.json;
};

export const createRoom = async (id: string, roomToAdd: Room, keycloak: KeycloakContext): Promise<string | undefined> => {
  const apiResponse = await postRequest<string>('room', keycloak, {
    OfficeId: id,
    Name: roomToAdd.name,
    Capacity: roomToAdd.capacity,
    IsReservableThroughFloorPlan: roomToAdd.isReservableThroughFloorPlan,
    ThresholdMinPercentage: roomToAdd.thresholdMinPercentage ?? null,
    ThresholdMaxPercentage: roomToAdd.thresholdMaxPercentage ?? null,
  });
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  return apiResponse.json;
};

/**
 * What deleting a room would cancel, counted from today in the office's own timezone, and what of
 * it would be taken off the office's floorplans.
 *
 * Counts rather than names: the administrator is deciding whether to proceed, not looking at who
 * is coming. `hasImpact` is the server's own answer to "does this need acknowledging" — the same
 * question that decides whether the delete endpoint demands the flag, so it is not re-derived
 * here from the four numbers.
 */
export interface RoomDeletionImpact {
  readonly roomId: string;
  readonly roomName: string;
  readonly from: string;
  readonly timeZone: string;
  readonly desks: number;
  readonly bookings: number;
  readonly people: number;
  readonly groupReservations: number;
  readonly groupReservationsLosingEveryDesk: number;
  /** Desks drawn for the room on the office's floorplans, all taken off them by the deletion. */
  readonly floorPlanDesks: number;
  /** Outlines of the room on the office's floorplans, taken off them by the deletion. */
  readonly floorPlanOutlines: number;
  /** The storeys whose floorplan the deletion changes, lowest first. */
  readonly floorPlanStoreys: readonly number[];
  readonly hasFloorPlanImpact: boolean;
  readonly hasImpact: boolean;
}

export const getRoomDeletionImpact = async (
  id: string,
  keycloak: KeycloakContext,
): Promise<RoomDeletionImpact | undefined> => {
  const apiResponse = await getRequest<RoomDeletionImpact>(`room/${id}/deletion-impact`, keycloak);

  if (apiResponse.error) {
    // Deliberately silent: the caller shows the failure inside the dialog, because a toast that
    // disappears behind an open modal would leave the delete button looking merely disabled.
    return undefined;
  }

  return apiResponse.json;
};

/**
 * Deletes a room, acknowledging what it cancels.
 *
 * `acknowledged` is not decoration. The server refuses with 409 when the room has future bookings
 * or group reservations, or is drawn on a floorplan, and the flag is absent, so nobody deletes a room's future by accident —
 * see DESK_RETIREMENT_PLAN.md 5.3.1.
 */
export const deleteRoom = async (id: string, keycloak: KeycloakContext, acknowledged = false): Promise<boolean> => {
  const apiResponse = await deleteRequestWithBody('room', keycloak, { Id: id, Acknowledged: acknowledged });

  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return false;
  }

  return true;
};

export const updateRoom = async (id: string, roomToUpdate: Room, keycloak: KeycloakContext): Promise<Room | undefined> => {
  const apiResponse = await putRequest<Room>('room', keycloak, {
    OfficeId: id,
    id: roomToUpdate.id,
    Name: roomToUpdate.name,
    Capacity: roomToUpdate.capacity,
    IsReservableThroughFloorPlan: roomToUpdate.isReservableThroughFloorPlan,
    ThresholdMinPercentage: roomToUpdate.thresholdMinPercentage ?? null,
    ThresholdMaxPercentage: roomToUpdate.thresholdMaxPercentage ?? null,
  });
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  return apiResponse.json;
};
